import { Tensor } from "onnxruntime-web";
import { renderBoxes } from "./renderBox";

export const detectImage = async (
  image,
  canvas,
  session,
  topk,
  iouThreshold,
  scoreThreshold,
  inputShape,
  setStats,
  showLabels = true
) => {

  const [modelW, modelH] = inputShape.slice(2);

  const { tensor, scale, dx, dy } = preprocessing(image, modelW, modelH);

  const outputs = await session.net.run({ images: tensor });

  const keys = Object.keys(outputs);
  const output0 = outputs[keys[0]];
  const output1 = keys.length > 1 ? outputs[keys[1]] : null;

  const boxes = [];
  const diameters = [];

  const N = output0.dims[1];
  const stride = output0.dims[2];

  for (let i = 0; i < N; i++) {

    const row = output0.data.slice(i * stride, (i + 1) * stride);

    let x1, y1, x2, y2, score;

    // -------- FORMAT DETECTION --------
    if (stride === 6) {

      const [a, b, c, d, e] = row;

      if (c > a && d > b) {
        // xyxy
        x1 = a; y1 = b; x2 = c; y2 = d; score = e;
      } else {
        // xywh
        let cx = a, cy = b, w = c, h = d;
        score = e;

        if (cx <= 1 && cy <= 1 && w <= 1 && h <= 1) {
          cx *= modelW; cy *= modelH;
          w *= modelW; h *= modelH;
        }

        x1 = cx - w / 2;
        y1 = cy - h / 2;
        x2 = cx + w / 2;
        y2 = cy + h / 2;
      }

    } else {
      let [cx, cy, w, h, obj] = row;
      score = obj;

      if (cx <= 1 && cy <= 1 && w <= 1 && h <= 1) {
        cx *= modelW; cy *= modelH;
        w *= modelW; h *= modelH;
      }

      x1 = cx - w / 2;
      y1 = cy - h / 2;
      x2 = cx + w / 2;
      y2 = cy + h / 2;
    }

    if (score < scoreThreshold) continue;

    // -------- LETTERBOX REVERSE --------
    x1 = (x1 - dx) / scale;
    y1 = (y1 - dy) / scale;
    x2 = (x2 - dx) / scale;
    y2 = (y2 - dy) / scale;

    // -------- CLAMP --------
    x1 = Math.max(0, Math.min(image.width, x1));
    y1 = Math.max(0, Math.min(image.height, y1));
    x2 = Math.max(0, Math.min(image.width, x2));
    y2 = Math.max(0, Math.min(image.height, y2));

    const bw = x2 - x1;
    const bh = y2 - y1;

    if (bw <= 2 || bh <= 2) continue;

    // -------- SEGMENTATION --------
    if (output1) {

      const mask = extractMask(output1, i, modelW, modelH);
      const refined = refineMask(mask);
      const instances = separateColonies(refined);

      instances.forEach(inst => {

        const [mx, my, mw, mh] = inst.bbox;

        let sx1 = (mx - dx) / scale;
        let sy1 = (my - dy) / scale;
        let sx2 = (mx + mw - dx) / scale;
        let sy2 = (my + mh - dy) / scale;

        sx1 = Math.max(0, Math.min(image.width, sx1));
        sy1 = Math.max(0, Math.min(image.height, sy1));
        sx2 = Math.max(0, Math.min(image.width, sx2));
        sy2 = Math.max(0, Math.min(image.height, sy2));

        const w = sx2 - sx1;
        const h = sy2 - sy1;

        if (w <= 2 || h <= 2) return;

        const diameter = (w + h) / 2;

        diameters.push(diameter);

        boxes.push({
          label: 0,
          probability: score,
          bounding: [sx1, sy1, sx2, sy2]
        });
      });

    } else {

      const diameter = (bw + bh) / 2;

      diameters.push(diameter);

      boxes.push({
        label: 0,
        probability: score,
        bounding: [x1, y1, x2, y2]
      });
    }
  }

  renderBoxes(canvas, boxes, showLabels);

  const stats = computeStats(diameters);
  if (setStats) setStats(stats);
};



// ================= HELPERS =================

function preprocessing(image, modelW, modelH) {

  const canvas = document.createElement("canvas");
  canvas.width = modelW;
  canvas.height = modelH;

  const ctx = canvas.getContext("2d");

  const scale = Math.min(modelW / image.width, modelH / image.height);

  const newW = Math.round(image.width * scale);
  const newH = Math.round(image.height * scale);

  const dx = (modelW - newW) / 2;
  const dy = (modelH - newH) / 2;

  ctx.fillStyle = "rgb(114,114,114)";
  ctx.fillRect(0, 0, modelW, modelH);
  ctx.drawImage(image, dx, dy, newW, newH);

  const imageData = ctx.getImageData(0, 0, modelW, modelH);
  const { data } = imageData;

  const floatData = new Float32Array(3 * modelW * modelH);

  for (let i = 0; i < modelW * modelH; i++) {
    floatData[i] = data[i * 4] / 255;
    floatData[i + modelW * modelH] = data[i * 4 + 1] / 255;
    floatData[i + 2 * modelW * modelH] = data[i * 4 + 2] / 255;
  }

  return {
    tensor: new Tensor("float32", floatData, [1, 3, modelH, modelW]),
    scale,
    dx,
    dy
  };
}


function extractMask(maskTensor, index, w, h) {
  const size = w * h;
  const start = index * size;
  return maskTensor.data.slice(start, start + size);
}

function refineMask(mask) {
  return mask.map(v => (v > 0.5 ? 1 : 0));
}

function separateColonies(mask) {

  const visited = new Set();
  const components = [];
  const width = Math.sqrt(mask.length);

  function dfs(i, comp) {
    if (visited.has(i) || mask[i] === 0) return;
    visited.add(i);
    comp.push(i);

    [i - 1, i + 1, i - width, i + width].forEach(n => {
      if (n >= 0 && n < mask.length) dfs(n, comp);
    });
  }

  for (let i = 0; i < mask.length; i++) {
    if (mask[i] === 1 && !visited.has(i)) {
      const comp = [];
      dfs(i, comp);
      if (comp.length > 20) components.push(comp);
    }
  }

  return components.map(comp => {
    const xs = comp.map(i => i % width);
    const ys = comp.map(i => Math.floor(i / width));

    const x1 = Math.min(...xs);
    const y1 = Math.min(...ys);
    const x2 = Math.max(...xs);
    const y2 = Math.max(...ys);

    return { bbox: [x1, y1, x2 - x1, y2 - y1] };
  });
}

function computeStats(diameters) {

  if (!diameters.length) {
    return {
      count: 0,
      avgDiameter: 0,
      minDiameter: 0,
      maxDiameter: 0,
      diameters: []
    };
  }

  const sum = diameters.reduce((a, b) => a + b, 0);

  return {
    count: diameters.length,
    avgDiameter: sum / diameters.length,
    minDiameter: Math.min(...diameters),
    maxDiameter: Math.max(...diameters),
    diameters
  };
}
